import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import SectionHeading from "@/components/base/SectionHeading";
import StatStrip from "@/components/feature/StatStrip";
import GoogleReviewCard from "@/components/feature/GoogleReviewCard";
import { achievements, coverageAreas, corporateClients, certifications } from "@/mocks/highlights";
import { stats } from "@/mocks/home";
import { googleReviews } from "@/mocks/reviews";
import { HERO_IMAGES } from "@/config/heroImages";

export default function HighlightsPage() {
  return (
    <>
      <PageHero
        eyebrow="Highlights"
        title="Highlights of Lahari Packers and Movers"
        subtitle="A quick look at what we have achieved since 2009, who trusts us and how far our service area reaches across Karnataka."
        crumbs={[{ label: "Home", href: "/" }, { label: "Highlights" }]}
        image={HERO_IMAGES.highlights}
      />

      <StatStrip stats={stats} />

      {/* Achievements */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Achievements"
            title="What we are proud of"
            subtitle="Numbers matter, but the reason customers return is the way their goods are handled on moving day."
          />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {achievements.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Assurance */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-14">
            <SectionHeading
              align="left"
              eyebrow="Customer Assurance"
              title="Promises printed into every estimate"
              subtitle="Nothing here is a marketing line. These are the standards we hold ourselves to on every single move."
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-1">
              {certifications.map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className={`${item.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{item.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Service Area"
            title="Areas we cover in and around Bangalore"
          />
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            {coverageAreas.map((area) => (
              <span
                key={area}
                className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2 text-sm text-foreground-700"
              >
                <i className="ri-map-pin-2-line text-primary-500" aria-hidden="true" />
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Clients + testimonials */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading eyebrow="Happy Customers" title="Businesses and families who moved with us" />

          <div className="mt-9 flex flex-wrap justify-center gap-3">
            {corporateClients.map((client) => (
              <span
                key={client}
                className="rounded-lg border border-background-200 bg-background-50 px-5 py-3 font-heading text-sm font-semibold text-foreground-800"
              >
                {client}
              </span>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {googleReviews.map((review) => (
              <GoogleReviewCard key={review.name} review={review} />
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Add your move to our list of happy customers"
        description="Get a free survey and a transparent, item-wise estimate from the Lahari team today."
      />
    </>
  );
}